import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Image,
  Platform,
  Animated,
  Easing
} from 'react-native';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../config/firebase';
import { useNavigation } from '../context/NavigationContext';
import FormHeader from '../components/FormHeader';
import FormInput from '../components/FormInput';
import PrimaryButton from '../components/PrimaryButton';
import DividerText from '../components/DividerText';
import GoogleButton from '../components/GoogleButton';
import CheckboxRow from '../components/CheckboxRow';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

const LoginScreen = () => {
  const navigation = useNavigation();
  const { signIn } = useAuth();
  const { palette } = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Animation values
  const formScale = useRef(new Animated.Value(0.8)).current;
  const formOpacity = useRef(new Animated.Value(0)).current;
  const leftCharX = useRef(new Animated.Value(-200)).current;
  const leftCharOpacity = useRef(new Animated.Value(0)).current;
  const rightCharX = useRef(new Animated.Value(200)).current;
  const rightCharOpacity = useRef(new Animated.Value(0)).current;
  
  // Staggered form element animations
  const headerOpacity = useRef(new Animated.Value(0)).current;
  const headerY = useRef(new Animated.Value(20)).current;
  const inputsOpacity = useRef(new Animated.Value(0)).current;
  const inputsY = useRef(new Animated.Value(20)).current;
  const dividerOpacity = useRef(new Animated.Value(0)).current;
  const socialOpacity = useRef(new Animated.Value(0)).current;
  const socialY = useRef(new Animated.Value(20)).current;
  const checkboxOpacity = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    // Main form entrance animation
    Animated.parallel([
      Animated.spring(formScale, {
        toValue: 1,
        tension: 50,
        friction: 8,
        useNativeDriver: true,
      }),
      Animated.timing(formOpacity, {
        toValue: 1,
        duration: 400,
        useNativeDriver: true,
      }),
    ]).start();

    // Character animations with delay
    Animated.sequence([
      Animated.delay(200),
      Animated.parallel([
        Animated.spring(leftCharX, {
          toValue: 0,
          tension: 40,
          friction: 8,
          useNativeDriver: true,
        }),
        Animated.timing(leftCharOpacity, {
          toValue: 1,
          duration: 500,
          useNativeDriver: true,
        }),
      ]),
    ]).start();

    Animated.sequence([
      Animated.delay(350),
      Animated.parallel([
        Animated.spring(rightCharX, {
          toValue: 0,
          tension: 40,
          friction: 8,
          useNativeDriver: true,
        }),
        Animated.timing(rightCharOpacity, {
          toValue: 1,
          duration: 500,
          useNativeDriver: true,
        }),
      ]),
    ]).start();

    // Staggered form element animations
    const staggerDelay = 100;
    
    Animated.sequence([
      Animated.delay(300),
      Animated.parallel([
        Animated.timing(headerOpacity, { toValue: 1, duration: 400, useNativeDriver: true }),
        Animated.spring(headerY, { toValue: 0, tension: 50, friction: 8, useNativeDriver: true }),
      ]),
    ]).start();

    Animated.sequence([
      Animated.delay(300 + staggerDelay),
      Animated.parallel([
        Animated.timing(inputsOpacity, { toValue: 1, duration: 400, useNativeDriver: true }),
        Animated.spring(inputsY, { toValue: 0, tension: 50, friction: 8, useNativeDriver: true }),
      ]),
    ]).start();

    Animated.sequence([
      Animated.delay(300 + staggerDelay * 2),
      Animated.timing(dividerOpacity, { toValue: 1, duration: 400, useNativeDriver: true }),
    ]).start();

    Animated.sequence([
      Animated.delay(300 + staggerDelay * 3),
      Animated.parallel([
        Animated.timing(socialOpacity, { toValue: 1, duration: 400, useNativeDriver: true }),
        Animated.spring(socialY, { toValue: 0, tension: 50, friction: 8, useNativeDriver: true }),
      ]),
    ]).start();

    Animated.sequence([
      Animated.delay(300 + staggerDelay * 4),
      Animated.timing(checkboxOpacity, { toValue: 1, duration: 400, useNativeDriver: true }),
    ]).start();
  }, []);

  const handleLogin = async () => {
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const result = await signIn(email, password);
      if (result && result.error) {
        // Format error message for better UX
        let errorMsg = result.error;
        if (result.error.includes('user-not-found')) {
          errorMsg = 'No account found with this email address';
        } else if (result.error.includes('wrong-password') || result.error.includes('invalid-credential')) {
          errorMsg = 'Incorrect email or password';
        } else if (result.error.includes('invalid-email')) {
          errorMsg = 'Invalid email address';
        } else if (result.error.includes('too-many-requests')) {
          errorMsg = 'Too many failed attempts. Please try again later';
        } else if (result.error.includes('network')) {
          errorMsg = 'Network error. Please check your connection';
        }
        setError(errorMsg);
      }
      // Success - navigation handled by AuthContext automatically
    } catch (error) {
      let errorMsg = error.message || 'An error occurred. Please try again.';
      if (error.message && error.message.includes('user-not-found')) {
        errorMsg = 'No account found with this email address';
      } else if (error.message && (error.message.includes('wrong-password') || error.message.includes('invalid-credential'))) {
        errorMsg = 'Incorrect email or password';
      }
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    setError('Google sign in will be implemented');
  };

  return (
    <View style={styles.container}>
      {/* Left Character */}
      <Animated.View 
        style={[
          styles.leftCharacter,
          {
            opacity: leftCharOpacity,
            transform: [{ translateX: leftCharX }],
          }
        ]}
      >
        <Image
          source={require('../assets/character_stills/FearIsFuel_CharacterStills_Tommy_Watch_v01_SL.png')}
          style={styles.characterImageLeft}
          resizeMode="contain"
        />
      </Animated.View>

      {/* Right Character */}
      <Animated.View 
        style={[
          styles.rightCharacter,
          {
            opacity: rightCharOpacity,
            transform: [{ translateX: rightCharX }],
          }
        ]}
      >
        <Image
          source={require('../assets/character_stills/FearIsFuel_CharacterStills_Tommy_HandsInAir_v01_SL.png')}
          style={styles.characterImageRight}
          resizeMode="contain"
        />
      </Animated.View>

      {/* Main Content */}
      <View style={styles.mainContent}>
        <Animated.View 
          style={[
            styles.formContainer,
            {
              opacity: formOpacity,
              transform: [{ scale: formScale }],
            }
          ]}
        >
          {/* Header */}
          <Animated.View style={{ opacity: headerOpacity, transform: [{ translateY: headerY }] }}>
            <FormHeader title="Welcome Back" subtitle="Please enter your credentials to log in." />
          </Animated.View>

          {/* Form Inputs */}
          <Animated.View 
            style={[
              styles.inputContainer,
              { opacity: inputsOpacity, transform: [{ translateY: inputsY }] }
            ]}
          >
            <FormInput
              placeholder="Email or Username"
              value={email}
              onChangeText={setEmail}
              keyboardType="email-address"
              autoCapitalize="none"
              returnKeyType="next"
            />

            <FormInput
              placeholder="Password"
              value={password}
              onChangeText={setPassword}
              secureTextEntry
              onSubmit={handleLogin}
            />

            {error && <Text style={[styles.errorText, { color: palette.white }]}>{error}</Text>}

            <PrimaryButton title={loading ? 'Loading...' : 'Log In'} onPress={handleLogin} disabled={loading} />

            {/* Switch to Register - right below login button */}
            <TouchableOpacity onPress={() => navigation.navigateTo('GettingStarted')} style={styles.switchAuthLink}>
              <Text style={styles.switchAuthText}>
                Don't have an account? <Text style={styles.switchAuthHighlight}>Sign Up</Text>
              </Text>
            </TouchableOpacity>
          </Animated.View>

          {/* Divider */}
          <Animated.View style={{ opacity: dividerOpacity, width: '100%' }}>
            <DividerText text="or continue with" />
          </Animated.View>

          {/* Social Sign In Buttons */}
          <Animated.View 
            style={[
              styles.socialButtonsContainer,
              { opacity: socialOpacity, transform: [{ translateY: socialY }] }
            ]}
          >
            <TouchableOpacity style={styles.socialButton} onPress={handleGoogleSignIn}>
              <Text style={styles.socialButtonText}>Sign In with Google</Text>
              <Image 
                source={require('../assets/icons/google.png')} 
                style={styles.socialIcon}
                resizeMode="contain"
              />
            </TouchableOpacity>
            
            <TouchableOpacity style={styles.socialButton} onPress={() => setError('Apple sign in will be implemented')}>
              <Text style={styles.socialButtonText}>Sign In with Apple</Text>
              <Image 
                source={require('../assets/icons/apple.png')} 
                style={styles.socialIconApple}
                resizeMode="contain"
              />
            </TouchableOpacity>
          </Animated.View>

          {/* Remember Me */}
          <Animated.View style={{ opacity: checkboxOpacity }}>
            <CheckboxRow label="Remember me" checked={rememberMe} onToggle={() => setRememberMe(!rememberMe)} />
          </Animated.View>

          {/* Forgot Password - at the bottom */}
          <Animated.View style={{ opacity: checkboxOpacity }}>
            <TouchableOpacity onPress={() => navigation.navigateTo('ForgotPassword')} style={styles.forgotPasswordLink}>
              <Text style={[styles.forgotPasswordText, { color: '#F1F1F1' }]}>
                Forgot Password?
              </Text>
            </TouchableOpacity>
          </Animated.View>
        </Animated.View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minHeight: '100vh',
    paddingHorizontal: 48,
    paddingTop: 140,
    paddingBottom: 60,
    overflow: 'hidden',
  },
  mainContent: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 48,
  },
  formContainer: {
    maxWidth: 700,
    width: '100%',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: 32,
    backgroundColor: '#FF4747',
    borderRadius: 40,
    paddingHorizontal: 120,
    paddingTop: 80,
    paddingBottom: 40,
      ...Platform.select({
          web: {
              boxShadow: '5px 7px 0px rgba(0,0,0,1)',
          },
          default: {
              shadowColor: '#000000',
              shadowOffset: { width: 5, height: 7 },
              shadowOpacity: 1,
              shadowRadius: 0,
              elevation: 8,
          },
      }),
  },
  header: {
    alignSelf: 'stretch',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    gap: 8,
  },
  title: {
    alignSelf: 'stretch',
    textAlign: 'center',
    color: '#fff',
    fontSize: 28,
    fontFamily: 'Public Sans',
    fontWeight: '700',
    lineHeight: 32,
  },
  subtitle: {
    alignSelf: 'stretch',
    textAlign: 'center',
    color: '#fff',
    fontSize: 15,
    fontFamily: 'Public Sans',
    fontWeight: '700',
    lineHeight: 20,
  },
  inputContainer: {
    alignSelf: 'stretch',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'stretch',
    gap: 16,
    marginTop: 12,
  },
  input: {
    alignSelf: 'stretch',
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: '#000',
    borderRadius: 22,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.16)',
    color: '#fff',
    fontSize: 15,
    fontFamily: 'Public Sans',
    fontWeight: '700',
    lineHeight: 20,
  },
  loginButton: {
    alignSelf: 'stretch',
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#FF0600',
    borderRadius: 22,
    justifyContent: 'center',
    alignItems: 'center',
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  loginButtonText: {
    color: '#000',
    fontSize: 15,
    fontFamily: 'Public Sans',
    fontWeight: '700',
    lineHeight: 20,
  },
  divider: {
    alignSelf: 'stretch',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
  },
  dividerText: {
    color: 'rgba(255, 255, 255, 0.60)',
    fontSize: 13,
    fontFamily: 'Public Sans',
    fontWeight: '700',
    lineHeight: 16,
  },
  socialButtonsContainer: {
    alignSelf: 'stretch',
    flexDirection: 'row',
    gap: 16,
  },
  socialButton: {
    flex: 1,
    height: 52,
    backgroundColor: '#000',
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
    gap: 12,
    position: 'relative',
    ...Platform.select({
      web: {
        boxShadow: '3px 3px 0px #f1f1f1',
      }
    }),
  },
  googleLogo: {
    width: 20,
    height: 20,
    position: 'relative',
    overflow: 'hidden',
  },
  socialButtonText: {
    textAlign: 'center',
    color: '#fff',
    fontSize: 15,
    fontFamily: 'Chivo',
    fontWeight: '600',
    lineHeight: 20,
    ...Platform.select({
      web: {
        textShadow: '2px 2px 1px rgba(0,0,0,1)',
      },
      default: {
        textShadowColor: 'rgba(0,0,0,1)',
        textShadowOffset: { width: 2, height: 2 },
        textShadowRadius: 1,
      },
    }),
  },
  socialIcon: {
    width: 20,
    height: 20,
  },
    socialIconApple: {
    width: 25,
    height: 25,
  },
  rememberMeContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: 8,
  },
  checkbox: {
    width: 16,
    height: 16,
    borderWidth: 1,
    borderColor: '#fff',
    borderRadius: 2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkboxInner: {
    width: 12,
    height: 12,
    backgroundColor: '#FF0600',
    borderRadius: 2,
  },
  rememberMeText: {
    color: '#fff',
    fontSize: 13,
    fontFamily: 'Public Sans',
    fontWeight: '700',
    lineHeight: 16,
  },
  errorText: {
    fontSize: 13,
    fontFamily: 'Public Sans',
    fontWeight: '300',
    textAlign: 'center',
    width: '100%',
    lineHeight: 16,
  },
  leftCharacter: {
    position: 'absolute',
    left: '12%',
    bottom: '-15%',
    zIndex: 1,
  },
  rightCharacter: {
    position: 'absolute',
    right: '0%',
    bottom: '-5%',
    zIndex: 1,
  },
  characterImageLeft: {
    width: 650,
    height: 650,
  },
  characterImageRight: {
    width: 620,
    height: 620,
  },
  switchAuthLink: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    marginTop: 8,
  },
  forgotPasswordLink: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    marginTop: 10,
  },
  forgotPasswordText: {
    fontSize: 14,
    fontFamily: 'Chivo',
    fontWeight: '600',
    ...Platform.select({
      web: {
        textShadow: '2px 2px 1px rgba(0,0,0,1)',
      },
      default: {
        textShadowColor: 'rgba(0,0,0,1)',
        textShadowOffset: { width: 2, height: 2 },
        textShadowRadius: 1,
      },
    }),
  },
  switchAuthText: {
    fontSize: 14,
    fontFamily: 'Chivo',
    fontWeight: '500',
    color: 'rgba(255,255,255,0.8)',
    ...Platform.select({
      web: {
        textShadow: '2px 2px 1px rgba(0,0,0,1)',
      },
      default: {
        textShadowColor: 'rgba(0,0,0,1)',
        textShadowOffset: { width: 2, height: 2 },
        textShadowRadius: 1,
      },
    }),
  },
  switchAuthHighlight: {
    color: '#F7DF7B',
    fontWeight: '700',
  },
});

export default LoginScreen;
